/**
 * Render a clock time for the "☕ back at H:MM AM/PM" chip on
 * `PresenceMemberRow` (#4166). Lives in the Activity package because the
 * Activity is the only consumer — Slack ops messages stay in ET via the
 * server-side `Intl.DateTimeFormat` in `break-escalation.blocks.ts`, but the
 * Activity runs in admins' browsers around the world and renders in the
 * viewer's local tz.
 */
export function formatBackAtTime(iso: string): string {
  return new Intl.DateTimeFormat(undefined, {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  }).format(new Date(iso));
}

/**
 * Render the "Fri 2:30–3:00 PM" window string for the pending-break-requests
 * alert strip (#4167). Drops the weekday prefix when both endpoints fall on
 * the viewer's "today" — keeps the cramped Activity row scannable for the
 * common case while still disambiguating multi-day rows.
 */
export function formatBreakWindow(startsAtIso: string, endsAtIso: string): string {
  const starts = new Date(startsAtIso);
  const ends = new Date(endsAtIso);
  const today = new Date();
  const sameDay = isSameLocalDate(starts, today) && isSameLocalDate(ends, today);

  const startStr = formatBackAtTime(startsAtIso);
  const endStr = formatBackAtTime(endsAtIso);
  if (sameDay) return `${startStr}–${endStr}`;

  const weekday = new Intl.DateTimeFormat(undefined, { weekday: 'short' }).format(starts);
  return `${weekday} ${startStr}–${endStr}`;
}

function isSameLocalDate(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}
